import { useQuery } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
export function useImageGalleryByYear(year) {
    return useQuery({
        queryKey: ['imageGallery', year],
        queryFn: () => apiService.getImageGalleryByYear(year),
        staleTime: 1000 * 60 * 10, // 10 minutes
        refetchOnWindowFocus: false,
        retry: 2,
    });
}
export function usePreviewImages(year) {
    return useQuery({
        queryKey: ['previewImages', year],
        queryFn: async () => {
            // Years that actually have image galleries available on the live site
            const availableYears = [2025, 2024, 2023, 2022, 2019, 2018, 2017, 2016, 2015, 2014, 2013, 2005, 2004, 2003];
            // Don't fetch data for future events
            if (year >= 2027) {
                return [];
            }
            // Only try to fetch data for years that actually have galleries
            if (availableYears.includes(year)) {
                try {
                    // First try to get preview images
                    const previewImages = await apiService.getPreviewImages(year);
                    // If preview is empty, fall back to first 3 images from full gallery
                    if (!previewImages || previewImages.length === 0) {
                        const fullGallery = await apiService.getImageGalleryByYear(year);
                        if (fullGallery?.images && fullGallery.images.length > 0) {
                            return fullGallery.images.slice(0, 3);
                        }
                    }
                    else {
                        return previewImages;
                    }
                }
                catch (error) {
                    console.warn(`Error fetching image data for ${year}:`, error);
                }
            }
            // For years without galleries, return empty array (no placeholder)
            return [];
        },
        staleTime: 1000 * 60 * 10, // 10 minutes
        refetchOnWindowFocus: false,
        retry: 1,
    });
}
// Hook to get preview images for all gallery years in a single API call
export function useMultiplePreviewImages(_years) {
    const query = useQuery({
        queryKey: ['allGalleryPreviews'],
        queryFn: () => apiService.getAllGalleryPreviews(),
        staleTime: 1000 * 60 * 10, // 10 minutes
        refetchOnWindowFocus: false,
        retry: 1,
    });
    return {
        data: (query.data || []).map(entry => ({
            year: entry.year,
            images: entry.images || [],
            isLoading: false,
            error: null,
        })),
        isLoading: query.isLoading,
        hasError: !!query.error,
    };
}
